import { AiAnalysisType } from 'src/app/domain/enumAiAnalysisType';

export interface RecordingLimits {
  maxMs: number;
  warningMs: number;
  criticalMs: number;
}

export const NOTE_RECORDING_LIMITS: RecordingLimits = {
  maxMs: 5 * 60 * 1000,
  warningMs: 4 * 60 * 1000,
  criticalMs: 4 * 60 * 1000 + 45 * 1000,
};

export const QUICK_RECORDING_LIMITS: RecordingLimits = {
  maxMs: 15 * 1000,
  warningMs: 9 * 1000,
  criticalMs: 13 * 1000,
};

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const TRANSCRIPTION_TIMEOUT_MS = 240 * 1000;
export const AUTO_RETRY_DELAY_MS = 3000;
export const MAX_AUTO_RETRIES = 1;
export const TIMER_TICK_MS = 250;

export const MAX_VOCABULARY_TERMS = 60;
export const MAX_VOCABULARY_TERM_LENGTH = 40;
export const MAX_VOCABULARY_CHARS = 600;

export const VOICE_PREFS_STORAGE_KEY = 'fn_voice_to_note';

export type TimerPhase = 'normal' | 'warning' | 'critical' | 'limit';

export function timerPhase(elapsedMs: number, limits: RecordingLimits): TimerPhase {
  if (elapsedMs >= limits.maxMs) { return 'limit'; }
  if (elapsedMs >= limits.criticalMs) { return 'critical'; }
  if (elapsedMs >= limits.warningMs) { return 'warning'; }
  return 'normal';
}

export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatTimer(elapsedMs: number, limits: RecordingLimits): string {
  const clamped = Math.min(Math.max(0, elapsedMs), limits.maxMs);
  return `${formatClock(clamped)} / ${formatClock(limits.maxMs)}`;
}

export function remainingLabel(elapsedMs: number, limits: RecordingLimits): string {
  const remainingMs = Math.max(0, limits.maxMs - elapsedMs);
  const seconds = Math.ceil(remainingMs / 1000);
  if (seconds >= 60) {
    const minutes = Math.round(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'} left`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'} left`;
}

export type DictationRole = 'police' | 'dfir' | 'privateInvestigator';

export interface DictationRoleOption {
  role: DictationRole;
  label: string;
  noteLabel: string;
  analysisType: AiAnalysisType;
}

export const DICTATION_ROLES: ReadonlyArray<DictationRoleOption> = [
  { role: 'police', label: 'Police', noteLabel: 'Police note', analysisType: AiAnalysisType.AudioTranscriptionRewriteAsNoteTextForPolice },
  { role: 'dfir', label: 'DFIR', noteLabel: 'DFIR note', analysisType: AiAnalysisType.AudioTranscriptionRewriteAsNoteTextForDFIR },
  { role: 'privateInvestigator', label: 'Private Investigator', noteLabel: 'Investigator note', analysisType: AiAnalysisType.AudioTranscriptionRewriteAsNoteTextForPrivateInvestigators },
];

export const DEFAULT_DICTATION_ROLE: DictationRole = 'police';

export function roleOption(role: DictationRole): DictationRoleOption {
  return DICTATION_ROLES.find(r => r.role === role) || DICTATION_ROLES[0];
}

export function isDictationRole(value: unknown): value is DictationRole {
  return DICTATION_ROLES.some(r => r.role === value);
}

export function normalizeVocabulary(raw: string | null | undefined): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  let totalChars = 0;
  const candidates = (raw || '').split(/[,\n;]+/);
  for (const candidate of candidates) {
    const term = candidate.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim().slice(0, MAX_VOCABULARY_TERM_LENGTH);
    if (!term) { continue; }
    const key = term.toLowerCase();
    if (seen.has(key)) { continue; }
    if (terms.length >= MAX_VOCABULARY_TERMS) { break; }
    if (totalChars + term.length > MAX_VOCABULARY_CHARS) { break; }
    seen.add(key);
    terms.push(term);
    totalChars += term.length;
  }
  return terms;
}

export function vocabularyToRequestField(raw: string | null | undefined): string {
  return normalizeVocabulary(raw).join(', ');
}

export type TranscriptVersion = 'verbatim' | 'refined' | 'investigative';

export function provenanceLine(version: TranscriptVersion, role: DictationRole): string {
  const text = version === 'verbatim'
    ? 'Source: voice dictation, transcribed by AI (verbatim transcript).'
    : version === 'refined'
      ? 'Source: voice dictation, transcribed by AI and lightly edited by AI for spelling and filler words.'
      : `Source: voice dictation, transcribed by AI and rewritten by AI as a ${roleOption(role).noteLabel.toLowerCase()}.`;
  return `<p><em>${text}</em></p>`;
}

export function composeNoteText(transcriptHtml: string, version: TranscriptVersion, role: DictationRole, addProvenance: boolean): string {
  if (!addProvenance) { return transcriptHtml; }
  return `${transcriptHtml}${provenanceLine(version, role)}`;
}

export type TranscriptionFailureKind = 'offline' | 'ready' | 'timeout' | 'session' | 'tooLarge' | 'rateLimit' | 'badRequest' | 'server' | 'unknown';

export const BACK_ONLINE_NOTICE: TranscriptionFailure = {
  kind: 'ready', title: 'Back online', detail: 'Your recording is still here and ready to transcribe.', canRetry: true, autoRetry: false,
};

export const KEPT_RECORDING_NOTICE: TranscriptionFailure = {
  kind: 'ready', title: 'Recording kept', detail: 'The dialog was closed while recording. Transcribe what you have, download it, or record again.', canRetry: true, autoRetry: false,
};

export const SILENT_MIC_GRACE_MS = 3000;
export const SILENT_MIC_LEVEL = 0.03;
export const STOP_WATCHDOG_MS = 3000;

export const DEVICE_LOST_NOTICE: TranscriptionFailure = {
  kind: 'ready', title: 'Microphone disconnected', detail: 'The microphone stopped sending audio, so the recording ended there. Transcribe what you have, download it, or record again.', canRetry: true, autoRetry: false,
};

export const EMPTY_TRANSCRIPT_NOTICE: TranscriptionFailure = {
  kind: 'unknown', title: 'Nothing was heard', detail: 'The transcript came back empty. Check the microphone and record again, or download the recording to check it.', canRetry: true, autoRetry: false,
};

export const RATE_LIMIT_EXHAUSTED_DETAIL = 'Still busy after retrying. Try again in a moment, or download the recording.';

export const EMPTY_RECORDING_FAILURE: MicrophoneFailure = {
  kind: 'unknown', title: 'Nothing was recorded', detail: 'The recorder produced no audio. Check the microphone is not muted, then press Record again.',
};

export interface TranscriptionFailure {
  kind: TranscriptionFailureKind;
  title: string;
  detail: string;
  canRetry: boolean;
  autoRetry: boolean;
}

export interface TranscriptionErrorLike {
  name?: string;
  status?: number;
  error?: { errorMessage?: string } | string | null;
  message?: string;
}

export function classifyTranscriptionError(err: TranscriptionErrorLike | null | undefined, online: boolean): TranscriptionFailure {
  const serverMessage = extractServerMessage(err);
  if (!online) {
    return failure('offline', 'You are offline', 'The recording is kept in this dialog until you leave the page. Transcribe it when you are back online, or download it now to be safe.', true, false);
  }
  if (err && err.name === 'TimeoutError') {
    return failure('timeout', 'Transcription timed out', 'The server did not answer within four minutes. Your recording is still here. Try again, or download it.', true, false);
  }
  const status = err && typeof err.status === 'number' ? err.status : undefined;
  if (status === 0) {
    return failure('offline', 'Could not reach the server', 'The request never arrived. Check your connection and try again. Your recording is still here.', true, false);
  }
  if (status === 401 || status === 403) {
    return failure('session', 'Your session has expired', 'Download the recording now so it is not lost, then sign in again and upload it.', false, false);
  }
  if (status === 413) {
    return failure('tooLarge', 'Recording too large', 'The server refused the file because of its size. Download it, trim it, and upload the shorter file.', false, false);
  }
  if (status === 429) {
    return failure('rateLimit', 'Transcription service is busy', 'Too many requests right now. Trying again automatically in a few seconds.', true, true);
  }
  if (status === 400 || status === 415 || status === 422) {
    return failure('badRequest', 'The audio could not be processed', serverMessage || 'The server rejected the recording. Try recording again, or upload a different file.', false, false);
  }
  if (status !== undefined && status >= 500) {
    return failure('server', 'Transcription failed on the server', serverMessage || 'The server reported an error. Your recording is still here, so try again or download it.', true, false);
  }
  return failure('unknown', 'Transcription failed', serverMessage || (err && err.message) || 'Something went wrong. Your recording is still here, so try again or download it.', true, false);
}

function extractServerMessage(err: TranscriptionErrorLike | null | undefined): string {
  if (!err || !err.error) { return ''; }
  if (typeof err.error === 'string') { return ''; }
  const message = err.error.errorMessage;
  return typeof message === 'string' ? message.slice(0, 300) : '';
}

function failure(kind: TranscriptionFailureKind, title: string, detail: string, canRetry: boolean, autoRetry: boolean): TranscriptionFailure {
  return { kind, title, detail, canRetry, autoRetry };
}

export type MicrophoneFailureKind = 'denied' | 'notFound' | 'inUse' | 'unsupported' | 'unknown';

export interface MicrophoneFailure {
  kind: MicrophoneFailureKind;
  title: string;
  detail: string;
}

export function classifyMicrophoneError(err: { name?: string; message?: string } | null | undefined, mediaDevicesSupported: boolean): MicrophoneFailure {
  if (!mediaDevicesSupported) {
    return { kind: 'unsupported', title: 'Recording is not available here', detail: 'This browser cannot record audio. Use the upload option with a recording made on your phone or another app.' };
  }
  const name = err && err.name ? err.name : '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return { kind: 'denied', title: 'Microphone access was blocked', detail: 'Allow the microphone for this site in your browser, then press Record again. Nothing was recorded.' };
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return { kind: 'notFound', title: 'No microphone was found', detail: 'Plug in or enable a microphone, then press Record again. If you chose a specific device, it may have been unplugged.' };
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return { kind: 'inUse', title: 'The microphone is busy', detail: 'Another app or tab is using the microphone. Close it and press Record again.' };
  }
  return { kind: 'unknown', title: 'Could not start recording', detail: (err && err.message) || 'The microphone could not be started. Try again, or upload a recording instead.' };
}

export function audioFileExtension(mimeType: string | null | undefined): string {
  const type = (mimeType || '').toLowerCase();
  if (type.includes('mp4') || type.includes('aac') || type.includes('m4a')) { return 'm4a'; }
  if (type.includes('mpeg') || type.includes('mp3')) { return 'mp3'; }
  if (type.includes('ogg')) { return 'ogg'; }
  if (type.includes('wav')) { return 'wav'; }
  return 'webm';
}

export function recordingFileName(mimeType: string | null | undefined, at: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}_${pad(at.getHours())}-${pad(at.getMinutes())}-${pad(at.getSeconds())}`;
  return `voice-note_${stamp}.${audioFileExtension(mimeType)}`;
}

export const ACCEPTED_UPLOAD_EXTENSIONS: ReadonlyArray<string> = ['webm', 'mp4', 'm4a', 'mp3', 'mpga', 'mpeg', 'wav', 'ogg', 'oga', 'opus', 'aac', 'flac'];
export const ACCEPTED_UPLOAD_CONTAINER_TYPES: ReadonlyArray<string> = ['video/webm', 'video/mp4', 'application/octet-stream'];

export function isAcceptableUploadType(type: string | null | undefined, name: string | null | undefined): boolean {
  const mime = (type || '').toLowerCase().split(';')[0].trim();
  const ext = ((name || '').split('.').pop() || '').toLowerCase();
  const knownExtension = ACCEPTED_UPLOAD_EXTENSIONS.includes(ext);
  if (mime.startsWith('audio/')) { return true; }
  if (ACCEPTED_UPLOAD_CONTAINER_TYPES.includes(mime)) { return knownExtension; }
  return mime === '' && knownExtension;
}

export function isAcceptableUpload(file: { type: string; size: number; name?: string } | null | undefined): { ok: boolean; reason: string } {
  if (!file) { return { ok: false, reason: 'No file was selected.' }; }
  if (!isAcceptableUploadType(file.type, file.name)) { return { ok: false, reason: 'Only audio files can be transcribed.' }; }
  if (file.size > MAX_UPLOAD_BYTES) { return { ok: false, reason: 'Audio files must be 25 MB or smaller.' }; }
  if (file.size === 0) { return { ok: false, reason: 'That file is empty.' }; }
  return { ok: true, reason: '' };
}

export function levelFromSamples(samples: ArrayLike<number>): number {
  if (!samples || samples.length === 0) { return 0; }
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i++) {
    const centered = (samples[i] - 128) / 128;
    sumSquares += centered * centered;
  }
  const rms = Math.sqrt(sumSquares / samples.length);
  return Math.min(1, rms * 3);
}

export interface VoicePreferences {
  role: DictationRole;
  vocabulary: string;
  addProvenance: boolean;
  deviceId: string;
}

export const DEFAULT_VOICE_PREFERENCES: VoicePreferences = {
  role: DEFAULT_DICTATION_ROLE,
  vocabulary: '',
  addProvenance: true,
  deviceId: '',
};

export function parseVoicePreferences(json: string | null | undefined): VoicePreferences {
  if (!json) { return { ...DEFAULT_VOICE_PREFERENCES }; }
  let parsed: any;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ...DEFAULT_VOICE_PREFERENCES };
  }
  if (!parsed || typeof parsed !== 'object') { return { ...DEFAULT_VOICE_PREFERENCES }; }
  return {
    role: isDictationRole(parsed.role) ? parsed.role : DEFAULT_DICTATION_ROLE,
    vocabulary: typeof parsed.vocabulary === 'string' ? normalizeVocabulary(parsed.vocabulary).join(', ') : '',
    addProvenance: typeof parsed.addProvenance === 'boolean' ? parsed.addProvenance : true,
    deviceId: typeof parsed.deviceId === 'string' ? parsed.deviceId.slice(0, 200) : '',
  };
}

export function serializeVoicePreferences(prefs: VoicePreferences): string {
  return JSON.stringify({
    role: prefs.role,
    vocabulary: normalizeVocabulary(prefs.vocabulary).join(', '),
    addProvenance: prefs.addProvenance,
    deviceId: prefs.deviceId || '',
  });
}

export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function voicePreferencesKey(userID: string | null | undefined): string {
  const id = (userID || '').trim();
  return id ? `${VOICE_PREFS_STORAGE_KEY}:${id}` : VOICE_PREFS_STORAGE_KEY;
}

export function loadVoicePreferences(storage: StorageLike | null | undefined, userID: string | null | undefined): VoicePreferences {
  if (!storage) { return { ...DEFAULT_VOICE_PREFERENCES }; }
  try {
    return parseVoicePreferences(storage.getItem(voicePreferencesKey(userID)));
  } catch {
    return { ...DEFAULT_VOICE_PREFERENCES };
  }
}

export function saveVoicePreferences(storage: StorageLike | null | undefined, userID: string | null | undefined, prefs: VoicePreferences): boolean {
  if (!storage) { return false; }
  try {
    storage.setItem(voicePreferencesKey(userID), serializeVoicePreferences(prefs));
    return true;
  } catch {
    return false;
  }
}

export function clearVoicePreferences(storage: StorageLike | null | undefined): number {
  if (!storage) { return 0; }
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && (key === VOICE_PREFS_STORAGE_KEY || key.startsWith(VOICE_PREFS_STORAGE_KEY + ':'))) { keys.push(key); }
    }
    keys.forEach(key => storage.removeItem(key));
    return keys.length;
  } catch {
    return 0;
  }
}
